// Request/response shapes for sections, links, and import/export. Used by the
// REST API, the MCP tools, and the web client.
import { z } from 'zod';
import {
  LINK_OPEN_IN,
  SECTION_LAYOUTS,
  linkIconSchema,
  userSettingsSchema,
} from './core';

export const sectionNameSchema = z.string().trim().min(1).max(64);
export const linkTitleSchema = z.string().trim().min(1).max(256);
export const linkDescriptionSchema = z.string().trim().max(1000);
export const tagSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[\p{L}\p{N}][\p{L}\p{N} _.-]{0,31}$/u, 'tags: 1-32 letters/digits');
export const tagsSchema = z
  .array(tagSchema)
  .max(20)
  .transform((tags) => [...new Set(tags)]);
// Validated further by normalizeUrl on the server.
export const linkUrlSchema = z.string().trim().min(1).max(2048);

export const sectionCreateSchema = z.object({
  name: sectionNameSchema,
  layout: z.enum(SECTION_LAYOUTS).optional(),
  collapsed: z.boolean().optional(),
  pinned: z.boolean().optional(),
});
export type SectionCreate = z.infer<typeof sectionCreateSchema>;

export const sectionUpdateSchema = sectionCreateSchema.partial();
export type SectionUpdate = z.infer<typeof sectionUpdateSchema>;

export const linkCreateSchema = z.object({
  url: linkUrlSchema,
  // Fetched from the page when omitted; falls back to the hostname.
  title: linkTitleSchema.optional(),
  description: linkDescriptionSchema.optional(),
  // Defaults to the user's "Inbox" section, created on demand.
  sectionId: z.uuid().optional(),
  tags: tagsSchema.optional(),
  icon: linkIconSchema.nullable().optional(),
  pinned: z.boolean().optional(),
  openIn: z.enum(LINK_OPEN_IN).optional(),
});
export type LinkCreate = z.infer<typeof linkCreateSchema>;

export const linkUpdateSchema = z.object({
  url: linkUrlSchema.optional(),
  title: linkTitleSchema.optional(),
  description: linkDescriptionSchema.optional(),
  sectionId: z.uuid().optional(),
  tags: tagsSchema.optional(),
  icon: linkIconSchema.nullable().optional(),
  pinned: z.boolean().optional(),
  openIn: z.enum(LINK_OPEN_IN).optional(),
});
export type LinkUpdate = z.infer<typeof linkUpdateSchema>;

export const linkQuerySchema = z.object({
  sectionId: z.uuid().optional(),
  tag: tagSchema.optional(),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
});
export type LinkQuery = z.infer<typeof linkQuerySchema>;

export const reorderSchema = z.object({
  ids: z.array(z.uuid()).max(5000),
});

// ------------------------------------------------------------ import/export

export const EXPORT_VERSION = 1;

export const exportLinkSchema = z.object({
  title: linkTitleSchema,
  url: linkUrlSchema,
  description: linkDescriptionSchema.default(''),
  tags: tagsSchema.default([]),
  icon: linkIconSchema.nullable().default(null),
  pinned: z.boolean().default(false),
  openIn: z.enum(LINK_OPEN_IN).default('default'),
});

export const exportSectionSchema = z.object({
  name: sectionNameSchema,
  layout: z.enum(SECTION_LAYOUTS).default('tiles'),
  collapsed: z.boolean().default(false),
  pinned: z.boolean().default(true),
  links: z.array(exportLinkSchema).max(5000).default([]),
});

export const exportDocumentSchema = z.object({
  version: z.literal(EXPORT_VERSION),
  settings: userSettingsSchema.optional(),
  sections: z.array(exportSectionSchema).max(500).default([]),
});
export type ExportDocument = z.infer<typeof exportDocumentSchema>;
export type ExportSection = z.infer<typeof exportSectionSchema>;

export const importRequestSchema = z.object({
  format: z.enum(['yaml', 'json', 'netscape']),
  content: z.string().max(5_000_000),
  // merge: add new sections/links, skip URLs you already have.
  // replace: delete all your sections and links first (and replace settings).
  mode: z.enum(['merge', 'replace']).default('merge'),
});
export type ImportRequest = z.infer<typeof importRequestSchema>;

export interface ImportResult {
  sectionsCreated: number;
  linksCreated: number;
  linksSkipped: number;
}
